import { useCallback, useEffect, useState } from "react";
import { CalendarPlus, Loader2, MapPin, Trash2, Video } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Chip, Empty, Err, Panel } from "@/components/community/kit";
import { when } from "@/lib/community";

const ANSWERS = [["yes", "Going"], ["maybe", "Maybe"], ["no", "Can't"]];

export const Meetings = ({ community }) => {
  const cid = community.id;
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({ title: "", when: "", location: "", link: "", agenda: "" });
  const [composing, setComposing] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/communities/${cid}/meetings`);
      setData(res);
    } catch (e) { setError(errMsg(e)); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy("create"); setError("");
    try {
      await api.post(`/communities/${cid}/meetings`, draft);
      setDraft({ title: "", when: "", location: "", link: "", agenda: "" });
      setComposing(false);
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const rsvp = async (id, answer) => {
    setBusy(`${id}-${answer}`);
    try { await api.post(`/communities/${cid}/meetings/${id}/rsvp`, { answer }); await load(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const remove = async (id) => {
    setBusy(id);
    try { await api.delete(`/communities/${cid}/meetings/${id}`); await load(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-gold" />;

  return (
    <div data-testid="community-meetings" className="space-y-4">
      <Err testId="meetings-error">{error}</Err>

      {data.can_manage && (
        <Panel testId="meetings-compose" title="Call a meeting"
          action={
            <Button data-testid="meetings-compose-toggle" size="sm" variant="outline"
              onClick={() => setComposing((v) => !v)}>
              {composing ? "Cancel" : <><CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> New meeting</>}
            </Button>
          }>
          {composing && (
            <div className="space-y-3">
              <Input data-testid="meeting-title" placeholder="Monthly members' meeting"
                value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              <div className="grid gap-3 sm:grid-cols-2">
                <Input data-testid="meeting-when" type="datetime-local" value={draft.when}
                  onChange={(e) => setDraft({ ...draft, when: e.target.value })} />
                <Input data-testid="meeting-location" placeholder="Where" value={draft.location}
                  onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
              </div>
              <Input data-testid="meeting-link" placeholder="Video link (optional)" value={draft.link}
                onChange={(e) => setDraft({ ...draft, link: e.target.value })} />
              <Textarea data-testid="meeting-agenda" rows={4} placeholder="Agenda"
                value={draft.agenda} onChange={(e) => setDraft({ ...draft, agenda: e.target.value })} />
              <Button data-testid="meeting-create" onClick={create}
                disabled={draft.title.trim().length < 3 || draft.when.length < 10 || busy === "create"}>
                {busy === "create" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Call the meeting
              </Button>
            </div>
          )}
        </Panel>
      )}

      {data.meetings.length === 0 ? (
        <Empty testId="meetings-empty">No meetings on the calendar.</Empty>
      ) : (
        data.meetings.map((m) => (
          <Panel key={m.id} testId={`meeting-row-${m.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-display text-base text-slate-100 md:text-lg">{m.title}</h3>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-gold">
                  {when(m.when)}
                </p>
                <div className="mt-2 space-y-1 text-xs text-slate-400">
                  {m.location && <p className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {m.location}</p>}
                  {m.link && (
                    <a href={m.link} target="_blank" rel="noreferrer" data-testid={`meeting-link-${m.id}`}
                      className="flex items-center gap-1.5 text-gold hover:underline">
                      <Video className="h-3 w-3" /> Join online
                    </a>
                  )}
                </div>
              </div>
              {data.can_manage && (
                <button data-testid={`meeting-delete-${m.id}`} onClick={() => remove(m.id)}
                  className="text-slate-500 hover:text-red-300">
                  {busy === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              )}
            </div>
            {m.agenda && (
              <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-300">{m.agenda}</p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {ANSWERS.map(([key, label]) => (
                <button key={key} data-testid={`meeting-rsvp-${key}-${m.id}`}
                  onClick={() => rsvp(m.id, key)} disabled={busy === `${m.id}-${key}`}
                  className={`rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                    m.my_rsvp === key ? "border-gold/40 bg-gold/10 text-gold"
                      : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
                  {label} · {m.rsvps?.[key] || 0}
                </button>
              ))}
              {m.host && <Chip>Called by {m.host}</Chip>}
            </div>
          </Panel>
        ))
      )}
    </div>
  );
};
